import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { MapContainer, ScaleControl, ZoomControl, FeatureGroup } from 'react-leaflet';
import L, { type Map as LeafletMap } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './MapView.css';

import {
  useMapData,
  useBaseMap,
  MapLayers,
  MapMarkers,
  MapPolygons,
  MapLoading,
  MapError,
  MapEmptyState,
} from './map';
import type { Point, CategorieMarqueur, Projet } from '../types/map';
import { MarkerModale } from './markerInfo/MarkerModale';
import { MarkerPanneau } from './markerInfo/MarkerPanneau';
import { renderTemplate, renderFallback, buildCssVariables } from './markerInfo/templateRenderer';
import MapConfigurator from './map/MapConfigurator';
import FullscreenControl from './map/FullscreenControl';
import { useLivingLabSearch } from './search/useLivingLabSearch';
import { SearchTriggerButton, SearchPanel, ResultsSheet } from './search';
import { parseUrlState, syncUrlState } from './search/useUrlState';

interface MapViewProps {
  center?: [number, number];
  zoom?: number;
  className?: string;
  height?: string;
  slug?: string;
}

export const MapView: React.FC<MapViewProps> = ({ 
  center = [48.8566, 2.3522], 
  zoom = 12,
  className = '',
  height = '100vh',
  slug
}) => {
  // Custom hooks for data and map configuration
  const { points, polygones, categories, projets, loading, error, mapKey, handleReload } = useMapData(slug);
  const { resolvedBaseMaps, couleurFond, mapConfig, configLoaded, serverBase } = useBaseMap(slug);

  // Lecture de l'URL pour les valeurs initiales (SPEC §2.4, W2.2 persistance).
  const urlState = useMemo(
    () => parseUrlState(resolvedBaseMaps, categories),
    [resolvedBaseMaps, categories]
  );

  // État du point sélectionné pour modale/panneau
  const [selectedPoint, setSelectedPoint] = useState<Point | null>(null);
  const [selectedCategorie, setSelectedCategorie] = useState<CategorieMarqueur | undefined>(undefined);
  // Projet actuellement mis en avant (marqueur(s) agrandi(s), §5.4).
  const [selectedProjetId, setSelectedProjetId] = useState<number | null>(null);

  // État de recherche centralisé : campus + catégories cochées + mots-clés (SPEC §2.4).
  // Au démarrage : fond de l'URL (ou par défaut) + catégories de l'URL (ou toutes).
  const {
    activeBaseMapId,
    setActiveBaseMapId,
    checkedCategorieIds,
    toggleCategorie,
    keywords,
    setKeywords,
    submittedKeywords,
    submitSearch,
    resetKeywords,
    sheetPosition,
    setSheetPosition,
    results,
  } = useLivingLabSearch(
    projets,
    categories,
    urlState.initialBaseMapId ?? resolvedBaseMaps[0]?.id,
    urlState.initialCheckedCategorieIds.size > 0 ? urlState.initialCheckedCategorieIds : undefined
  );

  // Référence à l'instance Leaflet (recentrage au clic picto).
  const mapRef = useRef<LeafletMap | null>(null);

  // Panneau « Votre recherche » : ouvert en desktop, fermé en mobile (§2.4).
  const [panelOpen, setPanelOpen] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 641px)').matches : true
  );

  // Index des catégories par id (pour les cartes de résultat et le détail).
  const categoriesById = useMemo(() => {
    const m = new Map<number, CategorieMarqueur>();
    categories.forEach((c) => {
      if (c.id != null) m.set(c.id, c);
    });
    return m;
  }, [categories]);


  // Chargement des CSS des catégories (une seule fois par catégorie)
  const loadedCssRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    categories.forEach(cat => {
      if (cat.id && cat.templateCss && !loadedCssRef.current.has(cat.id)) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = `${serverBase}/templates/cat-${cat.id}.css`;
        link.dataset.catId = String(cat.id);
        document.head.appendChild(link);
        loadedCssRef.current.add(cat.id);
      }
    });
  }, [categories, serverBase]);

  // Nettoyage des CSS au démontage
  useEffect(() => {
    const loadedCss = loadedCssRef.current;
    return () => {
      loadedCss.forEach(id => {
        const link = document.head.querySelector(`link[data-cat-id="${id}"]`);
        if (link) link.remove();
      });
      loadedCss.clear();
    };
  }, []);

  // Callback pour les modes modale/panneau (reçu depuis MapMarkers)
  const handleMarkerClick = useCallback((point: Point, categorie?: CategorieMarqueur) => {
    setSelectedPoint(point);
    setSelectedCategorie(categorie);
    setSelectedProjetId(point.projetId ?? null);
  }, []);

  const handleCloseOverlay = useCallback(() => {
    setSelectedPoint(null);
    setSelectedCategorie(undefined);
  }, []);

  // Recentre/ajuste la carte sur un projet (clic picto d'une carte de résultat, §5.4).
  // Tri-état : 0 emplacement → aucune action ; 1 → flyTo ; 2+ → fitBounds.
  const focusProjet = useCallback((projet: Projet, point?: Point) => {
    const map = mapRef.current;
    if (!map) return;
    setSelectedProjetId(projet.id);

    if (point && typeof point.latitude === 'number' && typeof point.longitude === 'number') {
      map.flyTo([point.latitude, point.longitude], Math.max(map.getZoom(), 5));
      return;
    }

    const validPoints = (projet.points || []).filter(
      (p): p is Point & { latitude: number; longitude: number } =>
        typeof p.latitude === 'number' && typeof p.longitude === 'number'
    );
    if (validPoints.length === 0) return; // projet sans emplacement : pas d'action carte
    if (validPoints.length === 1) {
      map.flyTo([validPoints[0].latitude, validPoints[0].longitude], Math.max(map.getZoom(), 5));
      return;
    }
    const bounds = L.latLngBounds(validPoints.map((p) => [p.latitude, p.longitude] as [number, number]));
    map.fitBounds(bounds, { padding: [50, 50] });
  }, []);

  const handlePictoClick = useCallback(
    (projet: Projet, point?: Point) => {
      focusProjet(projet, point);
    },
    [focusProjet]
  );

  // Ouvre un détail en mode popup (Leaflet) à l'emplacement du point.
  const openPopupDetail = useCallback((point: Point, categorie?: CategorieMarqueur) => {
    const map = mapRef.current;
    if (!map || typeof point.latitude !== 'number' || typeof point.longitude !== 'number') return;
    const html = categorie?.templateHtml
      ? renderTemplate(categorie.templateHtml, point, categorie)
      : renderFallback(point);
    const el = document.createElement('div');
    if (categorie?.id) el.className = `tpl-cat-${categorie.id}`;
    if (categorie) {
      const cssVars = buildCssVariables(point, categorie) as Record<string, string>;
      Object.entries(cssVars).forEach(([k, v]) => el.style.setProperty(k, String(v)));
    }
    // html déjà assaini (DOMPurify) par renderTemplate/renderFallback.
    el.innerHTML = html;
    L.popup({ maxWidth: 350, minWidth: 200 })
      .setLatLng([point.latitude, point.longitude])
      .setContent(el)
      .openOn(map);
  }, []);

  // « Afficher les détails » → ouvre selon le mode d'affichage de la catégorie (§5.4).
  const handleDetailsClick = useCallback(
    (projet: Projet, point?: Point) => {
      const resolvedPoint = point ?? (projet.points.length === 1 ? projet.points[0] : undefined);
      const categorie =
        projet.categorieMarqueur ??
        (projet.categorieId != null ? categoriesById.get(projet.categorieId) : undefined);
      focusProjet(projet, resolvedPoint);
      const mode = categorie?.modeAffichage ?? 'popup';
      if (mode === 'modale' || mode === 'panneau') {
        // Sans emplacement unique résolu, on synthétise un Point à partir du Projet
        // (le contenu affiché reste le socle du projet, cf. resolveEffectiveData côté API).
        const syntheticPoint: Point =
          resolvedPoint ?? {
            id: projet.id,
            nom: projet.nom,
            titre: projet.titre,
            description: projet.description,
            categorie: categorie?.slug || '',
            categorieId: projet.categorieId,
            donneesPersonnalisees: projet.donneesPersonnalisees,
            categorieMarqueur: categorie,
            projetId: projet.id,
          };
        setSelectedPoint(syntheticPoint);
        setSelectedCategorie(categorie);
      } else if (resolvedPoint) {
        openPopupDetail(resolvedPoint, categorie);
      }
      // Mode popup sans emplacement résolvable : pas d'ancrage possible, no-op.
    },
    [categoriesById, focusProjet, openPopupDetail]
  );

  // Validation de la recherche : ferme le panneau, ouvre le sheet en mi-ouvert (§2.4/§3.6).
  const handleSubmitSearch = useCallback(() => {
    submitSearch();
    setPanelOpen(false);
    setSheetPosition('half');
  }, [submitSearch, setSheetPosition]);

  // Ordre des Escape : modale d'abord, puis panneau (§6.3).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (selectedPoint) {
        handleCloseOverlay();
        return;
      }
      if (panelOpen) setPanelOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedPoint, panelOpen, handleCloseOverlay]);

  // Synchroniser l'état vers l'URL à chaque changement (SPEC §2.4, W2.2 persistance).
  useEffect(() => {
    syncUrlState(activeBaseMapId, checkedCategorieIds);
  }, [activeBaseMapId, checkedCategorieIds]);


  // Filtrer les points/polygones en fonction du fond de carte actif
  const visiblePoints = useMemo(() => {
    if (!activeBaseMapId) return points;
    return points.filter(p => p.baseMapIds && p.baseMapIds.length > 0 && (p.baseMapIds.includes('*') || p.baseMapIds.includes(activeBaseMapId)));
  }, [points, activeBaseMapId]);

  const visiblePolygones = useMemo(() => {
    if (!activeBaseMapId) return polygones;
    return polygones.filter(p => p.baseMapIds && p.baseMapIds.length > 0 && (p.baseMapIds.includes('*') || p.baseMapIds.includes(activeBaseMapId)));
  }, [polygones, activeBaseMapId]);

  // Grouper les points par catégorie pour les overlays individuels
  const { pointsByCategory, uncategorizedPoints } = useMemo(() => {
    const byCategory = new Map<number, Point[]>();
    const uncategorized: Point[] = [];

    for (const point of visiblePoints) {
      const catId = point.categorieId ?? point.categorieMarqueur?.id;
      if (catId != null) {
        const list = byCategory.get(catId);
        if (list) list.push(point);
        else byCategory.set(catId, [point]);
      } else {
        uncategorized.push(point);
      }
    }
    return { pointsByCategory: byCategory, uncategorizedPoints: uncategorized };
  }, [visiblePoints]);

  // Fond de carte actif (campus) — piloté par le SearchPanel.
  const activeBaseMap = useMemo(
    () => resolvedBaseMaps.find((b) => b.id === activeBaseMapId) ?? resolvedBaseMaps[0],
    [resolvedBaseMaps, activeBaseMapId]
  );

  // All initial params computed from loaded config (or props when no slug)
  const effectiveCenter: [number, number] = slug
    ? [mapConfig.centre.lat, mapConfig.centre.lng]
    : center;
  const effectiveZoom = slug ? mapConfig.zoom : zoom;

  // Wait for config to load before rendering the map (slug mode)
  const isReady = !slug || configLoaded;

  // Loading state (data loading OR config not yet loaded)
  if (loading || !isReady) {
    return <MapLoading className={className} height={height} />;
  }

  // Error state
  if (error) {
    return (
      <MapError 
        error={error} 
        className={className} 
        height={height} 
        onReload={handleReload} 
      />
    );
  }

  return (
    <div className={`map-container ${className}`} style={{ height }}>
      <MapContainer
        key={mapKey}
        ref={mapRef}
        center={effectiveCenter}
        zoom={effectiveZoom}
        minZoom={mapConfig.zoomMin}
        maxZoom={mapConfig.zoomMax}
        worldCopyJump={mapConfig.dupliquerCarte}
        zoomControl={false}
        style={{ height: '100%', width: '100%', ...(couleurFond ? { background: couleurFond } : {}) }}
        attributionControl={true}
      >
        {/* Applies dynamic constraints (interactions, maxBounds) */}
        <MapConfigurator
          mapConfig={mapConfig}
          activeBaseMapId={activeBaseMapId}
          resolvedBaseMaps={resolvedBaseMaps}
        />

        {/* Zoom control - only if enabled */}
        {mapConfig.afficherControles && (
          <ZoomControl position="topleft" />
        )}

        {/* Scale control */}
        {mapConfig.afficherEchelle && (
          <ScaleControl position="bottomleft" imperial={false} />
        )}

        {/* Fullscreen control */}
        {mapConfig.afficherPleinEcran && <FullscreenControl />}

        {/* Fond de carte actif (campus) — piloté par le SearchPanel (§3.4) */}
        {activeBaseMap && <MapLayers tileProps={activeBaseMap.tileProps} />}

        {/* Polygones du campus */}
        {visiblePolygones.length > 0 && (
          <FeatureGroup>
            <MapPolygons polygones={visiblePolygones} />
          </FeatureGroup>
        )}

        {/* Marqueurs par catégorie cochée (§3.5) */}
        {categories
          .filter(
            (cat) =>
              cat.id != null &&
              checkedCategorieIds.has(cat.id) &&
              (pointsByCategory.get(cat.id) || []).length > 0
          )
          .map((cat) => (
            <FeatureGroup key={cat.id}>
              <MapMarkers
                points={pointsByCategory.get(cat.id!)!}
                categories={categories}
                onMarkerClick={handleMarkerClick}
                selectedProjetId={selectedProjetId}
              />
            </FeatureGroup>
          ))}

        {/* Points sans catégorie */}
        {uncategorizedPoints.length > 0 && (
          <FeatureGroup>
            <MapMarkers
              points={uncategorizedPoints}
              categories={categories}
              onMarkerClick={handleMarkerClick}
              selectedProjetId={selectedProjetId}
            />
          </FeatureGroup>
        )}

        {/* Empty state indicator — only when there is genuinely no data at all */}
        <MapEmptyState show={points.length === 0 && polygones.length === 0 && !loading} />
      </MapContainer>

      {/* Découverte — overlays HTML (hors MapContainer) */}
      <SearchTriggerButton expanded={panelOpen} onClick={() => setPanelOpen((o) => !o)} />
      <SearchPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        baseMaps={resolvedBaseMaps}
        activeBaseMapId={activeBaseMapId}
        onBaseMapChange={setActiveBaseMapId}
        categories={categories}
        checkedCategorieIds={checkedCategorieIds}
        onToggleCategorie={toggleCategorie}
        keywords={keywords}
        onKeywordsChange={setKeywords}
        onSubmitSearch={handleSubmitSearch}
        submittedKeywords={submittedKeywords}
        onClearSearch={resetKeywords}
        illustrationUrl={mapConfig.illustrationUrl}
        illustrationUrlMobile={mapConfig.illustrationUrlMobile}
      />
      <ResultsSheet
        results={results}
        categoriesById={categoriesById}
        position={sheetPosition}
        onPositionChange={setSheetPosition}
        loading={false}
        themeColor={mapConfig.couleurTheme}
        onPictoClick={handlePictoClick}
        onDetailsClick={handleDetailsClick}
      />

      {/* Modale — rendue hors du MapContainer */}
      {selectedPoint && selectedCategorie?.modeAffichage === 'modale' && (
        <MarkerModale
          point={selectedPoint}
          categorie={selectedCategorie}
          onClose={handleCloseOverlay}
        />
      )}

      {/* Panneau latéral — rendu hors du MapContainer */}
      {selectedPoint && selectedCategorie?.modeAffichage === 'panneau' && (
        <MarkerPanneau
          point={selectedPoint}
          categorie={selectedCategorie}
          onClose={handleCloseOverlay}
        />
      )}
    </div>
  );
};

export default MapView;
